// src/components/admin/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/admin/Dashboard.css';

interface Activity {
  id: string;
  title: string;
  date: string;
  status: string;
}

interface Donation {
  id: string;
  amount: number;
  donor: string;
  date: string;
  campaign: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('Admin');
  const [stats, setStats] = useState({
    totalDonations: 0,
    monthlyDonations: 0,
    activeActivities: 0,
    subscribers: 0
  });
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setUserName(user.name || 'Admin');
    }

    // Load dashboard data (in a real app, this would be API calls)
    loadMockData();
  }, [navigate]);

  const loadMockData = () => {
    // Mock statistics data
    setStats({
      totalDonations: 45750,
      monthlyDonations: 5280,
      activeActivities: 12,
      subscribers: 824
    });

    // Mock recent activities
    setRecentActivities([
      { id: '1', title: 'Community Garden Planting', date: '2025-05-01', status: 'Active' },
      { id: '2', title: 'Youth Leadership Workshop', date: '2025-05-10', status: 'Upcoming' },
      { id: '3', title: 'Clean Energy Conference', date: '2025-04-28', status: 'Completed' },
      { id: '4', title: 'Wildlife Conservation Drive', date: '2025-05-15', status: 'Upcoming' }
    ]);

    // Mock recent donations
    setRecentDonations([
      { id: '1', amount: 250, donor: 'John D.', date: '2025-05-03', campaign: 'General Fund' },
      { id: '2', amount: 1000, donor: 'Smith Foundation', date: '2025-05-02', campaign: 'Youth Program' },
      { id: '3', amount: 75, donor: 'Anonymous', date: '2025-05-01', campaign: 'General Fund' },
      { id: '4', amount: 500, donor: 'Emily W.', date: '2025-04-30', campaign: 'Clean Energy' }
    ]);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <div className="welcome-message">
          Welcome back, <span>{userName}</span>
        </div>
      </header>

      <section className="stats-overview">
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.totalDonations)}</div>
          <div className="stat-label">Total Donations</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatCurrency(stats.monthlyDonations)}</div>
          <div className="stat-label">Monthly Donations</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.activeActivities}</div>
          <div className="stat-label">Active Activities</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.subscribers}</div>
          <div className="stat-label">Newsletter Subscribers</div>
        </div>
      </section>

      <div className="dashboard-content-grid">
        <section className="recent-activities">
          <div className="section-header">
            <h2>Recent Activities</h2>
            <button className="manage-button">Manage All</button>
          </div>
          <div className="activity-list">
            <table>
              <thead>
                <tr>
                  <th>Activity Name</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentActivities.map(activity => (
                  <tr key={activity.id}>
                    <td>{activity.title}</td>
                    <td>{activity.date}</td>
                    <td>
                      <span className={`status ${activity.status.toLowerCase()}`}>
                        {activity.status}
                      </span>
                    </td>
                    <td>
                      <button className="action-btn edit">Edit</button>
                      <button className="action-btn view">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="recent-donations">
          <div className="section-header">
            <h2>Recent Donations</h2>
            <button className="manage-button">View All</button>
          </div>
          <div className="donations-list">
            <table>
              <thead>
                <tr>
                  <th>Donor</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Campaign</th>
                </tr>
              </thead>
              <tbody>
                {recentDonations.map(donation => (
                  <tr key={donation.id}>
                    <td>{donation.donor}</td>
                    <td>{formatCurrency(donation.amount)}</td>
                    <td>{donation.date}</td>
                    <td>{donation.campaign}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-buttons">
            <button className="quick-action-btn">
              <i className="icon-activity"></i>
              Create Activity
            </button>
            <button className="quick-action-btn">
              <i className="icon-blog"></i>
              New Blog Post
            </button>
            <button className="quick-action-btn">
              <i className="icon-donation"></i>
              Record Donation
            </button>
            <button className="quick-action-btn">
              <i className="icon-newsletter"></i>
              Send Newsletter
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;